import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  useMediaQuery
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import type { DashboardPeriodo } from '../hooks/useDashboard.js';
import { useDashboardGeoEstados, useDashboardGeoCidades } from '../hooks/useDashboard.js';

// AIDEV-NOTE: Mapa coroplético do Brasil — estados coloridos por volume de vendas.
// Desktop: split view mapa + painel de cidades. Mobile: navegação click (substitui mapa).

// AIDEV-NOTE: GeoJSON com 27 estados, propriedades: sigla (UF), name, codigo_ibg
const IBGE_GEOJSON_URL =
  'https://raw.githubusercontent.com/codeforgermany/click_that_hood/main/public/data/brazil-states.geojson';

const IBGE_CODE_TO_UF: Record<string, string> = {
  '11': 'RO',
  '12': 'AC',
  '13': 'AM',
  '14': 'RR',
  '15': 'PA',
  '16': 'AP',
  '17': 'TO',
  '21': 'MA',
  '22': 'PI',
  '23': 'CE',
  '24': 'RN',
  '25': 'PB',
  '26': 'PE',
  '27': 'AL',
  '28': 'SE',
  '29': 'BA',
  '31': 'MG',
  '32': 'ES',
  '33': 'RJ',
  '35': 'SP',
  '41': 'PR',
  '42': 'SC',
  '43': 'RS',
  '50': 'MS',
  '51': 'MT',
  '52': 'GO',
  '53': 'DF'
};

const UF_NOMES: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AM: 'Amazonas',
  AP: 'Amapá',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MG: 'Minas Gerais',
  MS: 'Mato Grosso do Sul',
  MT: 'Mato Grosso',
  PA: 'Pará',
  PB: 'Paraíba',
  PE: 'Pernambuco',
  PI: 'Piauí',
  PR: 'Paraná',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RO: 'Rondônia',
  RR: 'Roraima',
  RS: 'Rio Grande do Sul',
  SC: 'Santa Catarina',
  SE: 'Sergipe',
  SP: 'São Paulo',
  TO: 'Tocantins'
};

const BRASIL_BOUNDS: [[number, number], [number, number]] = [
  [-33.75, -73.99],
  [5.27, -28.85]
];

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  });
}

function getUFFromFeature(feature: any): string {
  const sigla = String(feature?.properties?.sigla ?? '')
    .trim()
    .toUpperCase();
  if (sigla.length === 2 && sigla in UF_NOMES) return sigla;
  const numId = String(feature?.id ?? feature?.properties?.codigo_ibg ?? '').trim();
  return IBGE_CODE_TO_UF[numId] ?? '';
}

interface Props {
  periodo: DashboardPeriodo;
}

const MAP_HEIGHT = 380;

export function BrasilMapaChart({ periodo }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedUF, setSelectedUF] = useState<string | null>(null);

  const {
    data: geoJson,
    isLoading: loadingGeo,
    isError: errorGeo
  } = useQuery({
    queryKey: ['brasil-geojson-v2'],
    queryFn: async () => {
      const res = await fetch(IBGE_GEOJSON_URL);
      if (!res.ok) throw new Error('Falha ao carregar GeoJSON');
      return res.json();
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1
  });

  const { data: estadosDados = [] } = useDashboardGeoEstados(periodo);
  const { data: cidadesDados = [], isLoading: loadingCidades } = useDashboardGeoCidades(
    selectedUF,
    periodo
  );

  const estadosMap = new Map(estadosDados.map((e) => [e.uf, e]));
  const maxTotal = estadosDados.reduce((max, e) => Math.max(max, e.total), 0);

  const getStyle = useCallback(
    (feature: any) => {
      const uf = getUFFromFeature(feature);
      const total = estadosMap.get(uf)?.total ?? 0;
      const intensity = maxTotal > 0 && total > 0 ? total / maxTotal : 0;
      return {
        fillColor: intensity > 0 ? theme.palette.primary.main : '#e0e0e0',
        fillOpacity: intensity > 0 ? 0.15 + intensity * 0.75 : 0.4,
        color: '#fff',
        weight: 1
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [estadosDados, maxTotal, theme.palette.primary.main]
  );

  const onEachFeature = useCallback(
    (feature: any, layer: any) => {
      const uf = getUFFromFeature(feature);
      const nome = UF_NOMES[uf] ?? uf;
      const est = estadosMap.get(uf);
      const tip = est
        ? `<b>${nome} (${uf})</b><br>${formatCurrency(est.total)}<br>${est.pedidos} pedido(s)`
        : `<b>${nome} (${uf})</b><br>Sem pedidos no período`;
      layer.bindTooltip(tip, { sticky: true });
      layer.on({
        mouseover() {
          layer.setStyle({ weight: 2, color: '#333' });
        },
        mouseout() {
          layer.setStyle({ weight: 1, color: '#fff' });
        },
        click() {
          if (uf) setSelectedUF(uf);
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [estadosDados]
  );

  // ── Mobile: navega para visão de cidades ao clicar num estado ─────────────
  if (isMobile && selectedUF) {
    const nomeEstado = UF_NOMES[selectedUF] ?? selectedUF;
    const totalEstado = estadosMap.get(selectedUF)?.total ?? 0;

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setSelectedUF(null)}>
              Todos os estados
            </Button>
            <Typography variant="subtitle1" fontWeight={600}>
              {nomeEstado} ({selectedUF})
            </Typography>
            <Chip
              label={formatCurrency(totalEstado)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <CidadesTabela cidades={cidadesDados} loading={loadingCidades} />
        </CardContent>
      </Card>
    );
  }

  // ── Mapa (desktop: split; mobile: mapa sozinho) ───────────────────────────
  return (
    <Card>
      <CardContent>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Vendas por estado
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Clique em um estado para ver as cidades
          </Typography>
        </Box>

        {errorGeo && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Não foi possível carregar o mapa. Verifique a conexão com a internet.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* ── Coluna do mapa ─────────────────────────────────────────── */}
          <Box sx={{ flex: { xs: 1, md: 3 }, minWidth: 0 }}>
            {loadingGeo && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {geoJson && (
              <Box
                sx={{
                  height: MAP_HEIGHT,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: '#f0f4f8',
                  '& .leaflet-container': { background: '#f0f4f8' }
                }}
              >
                <MapContainer
                  bounds={BRASIL_BOUNDS}
                  scrollWheelZoom={false}
                  dragging={false}
                  doubleClickZoom={false}
                  zoomControl={false}
                  attributionControl={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  {/* AIDEV-NOTE: key força re-montagem do layer ao mudar período ou dados */}
                  <GeoJSON
                    key={`${periodo.mes}-${periodo.ano}-${estadosDados.length}`}
                    data={geoJson}
                    style={getStyle}
                    onEachFeature={onEachFeature}
                  />
                </MapContainer>
              </Box>
            )}

            {estadosDados.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1 }}>
                {[
                  { color: '#e0e0e0', label: 'Sem vendas' },
                  { color: theme.palette.primary.light, label: 'Menor', opacity: 0.6 },
                  { color: theme.palette.primary.main, label: 'Maior' }
                ].map(({ color, label, opacity }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        bgcolor: color,
                        opacity: opacity ?? 1
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* ── Painel de cidades (desktop apenas) ─────────────────────── */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              flex: 2,
              minWidth: 220,
              height: MAP_HEIGHT,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'background.paper'
            }}
          >
            {!selectedUF ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 2
                }}
              >
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Clique em um estado
                  <br />
                  para ver as cidades
                </Typography>
              </Box>
            ) : (
              <>
                {/* Cabeçalho do estado selecionado */}
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                    {UF_NOMES[selectedUF] ?? selectedUF}
                  </Typography>
                  <Chip label={selectedUF} size="small" />
                  {estadosMap.get(selectedUF) && (
                    <Typography variant="caption" color="primary.main" fontWeight={700}>
                      {formatCurrency(estadosMap.get(selectedUF)!.total)}
                    </Typography>
                  )}
                </Box>

                {/* Tabela de cidades com scroll */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  <CidadesTabela cidades={cidadesDados} loading={loadingCidades} compact />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Sub-componente reutilizado em mobile e desktop ────────────────────────────
interface CidadesTabelaProps {
  cidades: ReturnType<typeof useDashboardGeoCidades>['data'] extends (infer T)[] | undefined
    ? T[]
    : never[];
  loading: boolean;
  compact?: boolean;
}

function CidadesTabela({ cidades = [], loading, compact }: CidadesTabelaProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (cidades.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Sem pedidos no período para este estado.
      </Typography>
    );
  }

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, pl: 1.5, width: 28 }}>#</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Cidade</TableCell>
          <TableCell sx={{ fontWeight: 600 }} align="right">
            Faturamento
          </TableCell>
          {!compact && (
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Ped.
            </TableCell>
          )}
          <TableCell sx={{ fontWeight: 600 }} align="right">
            %
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {cidades.map((c, i) => (
          <TableRow key={c.cidade} hover>
            <TableCell
              sx={{
                pl: 1.5,
                color: i < 3 ? 'primary.main' : 'text.disabled',
                fontWeight: i < 3 ? 700 : 400
              }}
            >
              {i + 1}
            </TableCell>
            <TableCell
              sx={{
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {c.cidade}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {formatCurrency(c.total)}
            </TableCell>
            {!compact && <TableCell align="right">{c.pedidos}</TableCell>}
            <TableCell align="right" sx={{ color: 'text.secondary' }}>
              {c.percentual.toFixed(1)}%
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
